import React, {
  useState,
  useCallback,
  useEffect,
  useContext,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { DrawerProvider } from "../shared/components/EMPDrawer/DrawerContext";
import { useOrders } from "../shared/hooks/useOrders";
import OrdersFilters from "./OrdersFilters";
import OrdersSorting from "./OrdersSorting";
import OrdersTable from "./OrdersTable";
import OrderTableSkeleton from "./OrderTableSkeleton";
import { sortOrders } from "../utils/orderSortUtils";
import {
  mapOrderRequestType,
  mapVehicleSource,
  mapOrderStatus,
  getFilteredArray,
  filterLincolnOrders,
} from "./utils/OrderFilterUtils";
import * as OrdersPageAnalytics from "../shared/services/analytics/OrdersPageAnalytics";
import { UserContext } from "../shared/context/userContext";
import EMPButton from "../shared/components/EMPButton/EMPButton";
import { useOrderFilters } from "../shared/hooks/useOrderFilters";
import EMPBreadcrumbs from "../shared/components/EMPBreadcrumbs/EMPBreadcrumbs";
import { useTranslation } from "react-i18next";
import { isCanadianPreorderUser } from "../utils/canadianPreorderExperience";
import { isLincolnClientHandlingUser } from "./filterConfigs";

const BRAND_LAYOUT = {
  lincoln: {
    tabletQuery: "(max-width:767px)",
    defaultSort: "nearest",
    sidebarWidth: "154px",
    sectionSx: {
      py: "20px",
      px: { xs: "20px", sm: "32px", md: "40px" },
      bgcolor: "#fafafa",
      columnGap: "24px",
      rowGap: "18px",
      color: "#333",
      fontFamily: "ProximaNovaRegular, Arial, sans-serif",
      "& .MuiBreadcrumbs-root, & .MuiBreadcrumbs-root p": {
        fontSize: "12px",
        lineHeight: "16px",
        color: "#555",
      },
      "& .MuiBreadcrumbs-root a": {
        fontSize: "12px",
        lineHeight: "16px",
        color: "#555",
      },
    },
    loadMoreMt: "16px",
    loadMoreSx: {
      "& button": {
        fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
        fontSize: "12px",
        letterSpacing: 0,
        color: "#485b65",
        textTransform: "none",
      },
    },
    loadMoreLabel: "Load more",
  },
  ford: {
    tabletQuery: "(max-width:1024px)",
    defaultSort: "newest",
    sidebarWidth: "240px",
    sectionSx: { py: 4, px: { xs: 4, sm: 4, md: 4, lg: 6 } },
    loadMoreMt: 4,
    loadMoreSx: {},
    loadMoreLabel: "Load More Orders",
  },
};

const getOrdersLayout = (
  isLincoln,
  isCanadianPreorder,
  t,
  isUpdatedLincolnUser
) => {
  if (isLincoln) {
    return isUpdatedLincolnUser
      ? { ...BRAND_LAYOUT.lincoln, defaultSort: "newest" }
      : BRAND_LAYOUT.lincoln;
  }
  if (!isCanadianPreorder) return BRAND_LAYOUT.ford;

  return {
    ...BRAND_LAYOUT.ford,
    defaultSort: "nearest",
    sidebarWidth: "minmax(0, 190px)",
    loadMoreLabel: t("CanadianPreorderOrders.loadMore", {
      defaultValue: "Load more",
    }),
  };
};

// Ford filters are applied server side, so map the selections to the API body
const buildFilterRequestBody = (selectedFilters, canadianPreorder = false) => ({
  actionRequired: selectedFilters.actionRequired?.includes("yes") ? true : null,
  orderRequestType: canadianPreorder
    ? []
    : getFilteredArray(selectedFilters.orderRequestType, mapOrderRequestType),
  vehicleStatus: canadianPreorder
    ? []
    : getFilteredArray(selectedFilters.vehicleSource, mapVehicleSource),
  orderStatus: getFilteredArray(selectedFilters.orderStatus, mapOrderStatus),
});

const hasSelectedFilters = (selectedFilters) =>
  Object.values(selectedFilters).some((values) => values.length > 0);

const hasActiveLincolnFilters = (isLincoln, selectedFilters) =>
  isLincoln && hasSelectedFilters(selectedFilters);

const getOrdersRequestFilters = (
  isLincoln,
  isCanadianPreorder,
  selectedFilters
) => {
  if (isLincoln) {
    return {
      actionRequired: null,
      orderRequestType: [],
      vehicleStatus: [],
      orderStatus: [],
    };
  }

  return buildFilterRequestBody(selectedFilters, isCanadianPreorder);
};

const getSortValue = (sortState, sortIdentity, defaultSort) =>
  sortState.identity === sortIdentity ? sortState.value : defaultSort;

const getSortState = (current, sortIdentity, defaultSort) =>
  current.identity === sortIdentity
    ? current
    : { identity: sortIdentity, value: defaultSort };

const getFetchedOrders = (pages) =>
  pages?.flatMap((page) => page.orderResponses || []) || [];

const getSortedOrders = (
  orders,
  isLincoln,
  selectedFilters,
  sortValue,
  isCanadianPreorder
) => {
  const ordersToSort = isLincoln
    ? filterLincolnOrders(orders, selectedFilters)
    : orders;
  const getDeliveryDate = isCanadianPreorder
    ? (order) => order.estimatedDeliveryDate ?? order.delivery
    : undefined;

  return sortOrders(ordersToSort, sortValue, getDeliveryDate);
};

const shouldLoadNextLincolnPage = (
  hasLincolnFilters,
  hasNextPage,
  isFetchingNextPage,
  isFetchNextPageError
) =>
  hasLincolnFilters &&
  hasNextPage &&
  !isFetchingNextPage &&
  !isFetchNextPageError;

const OrdersPageTitle = ({ title }) => (
  <Typography
    component="h1"
    sx={{
      gridColumn: "1 / -1",
      fontSize: 28,
      fontWeight: 600,
      lineHeight: "36px",
      letterSpacing: 0,
      color: "#111",
      mb: "8px",
    }}
  >
    {title}
  </Typography>
);

OrdersPageTitle.propTypes = {
  title: PropTypes.string.isRequired,
};

const LoadMoreButton = ({ layout, onClick, disabled }) => (
  <Box
    display="flex"
    justifyContent="center"
    mt={layout.loadMoreMt}
    mb={2}
    sx={layout.loadMoreSx}
  >
    <EMPButton
      variant="text"
      onClick={onClick}
      testId="load-more-button"
      disabled={disabled}
    >
      {layout.loadMoreLabel}
    </EMPButton>
  </Box>
);

LoadMoreButton.propTypes = {
  layout: PropTypes.shape({
    loadMoreMt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    loadMoreSx: PropTypes.object,
    loadMoreLabel: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

const Orders = () => {
  const { user } = useContext(UserContext);
  const { t } = useTranslation("emp");
  const isLincoln = user?.brand === "L";
  const isUpdatedLincolnUser = isLincolnClientHandlingUser(user);
  const isCanadianPreorder = isCanadianPreorderUser(user);
  const layout = getOrdersLayout(
    isLincoln,
    isCanadianPreorder,
    t,
    isUpdatedLincolnUser
  );
  const isTablet = useMediaQuery(layout.tabletQuery);
  const defaultSort = layout.defaultSort;
  const sortIdentity = JSON.stringify([
    user?.market,
    user?.brand,
    user?.dealerId,
    user?.salesCode,
    defaultSort,
  ]);
  const [sortState, setSortState] = useState({
    identity: sortIdentity,
    value: defaultSort,
  });
  const sortValue = getSortValue(sortState, sortIdentity, defaultSort);
  useEffect(() => {
    setSortState((current) => getSortState(current, sortIdentity, defaultSort));
  }, [sortIdentity, defaultSort]);
  const filterControls = useOrderFilters(null, user);
  const { selectedFilters, handleFiltersApplied } = filterControls;

  const handleFilters = useCallback(
    (selections) => {
      if (!isLincoln) handleFiltersApplied(selections);
    },
    [isLincoln, handleFiltersApplied]
  );

  const filters = useMemo(
    () =>
      getOrdersRequestFilters(isLincoln, isCanadianPreorder, selectedFilters),
    [isLincoln, isCanadianPreorder, selectedFilters]
  );

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useOrders(filters);

  useEffect(() => {
    // Track page load when component mounts
    OrdersPageAnalytics.trackOrdersPageLoad(user);
  }, [user]);

  const handleSortChange = (newSortValue) => {
    OrdersPageAnalytics.trackSortingChange(newSortValue, user);
    setSortState({ identity: sortIdentity, value: newSortValue });
  };

  const allFetchedOrders = useMemo(
    () => getFetchedOrders(data?.pages),
    [data?.pages]
  );

  const sortedOrders = getSortedOrders(
    allFetchedOrders,
    isLincoln,
    filterControls.selectedFilters,
    sortValue,
    isCanadianPreorder
  );

  const hasLincolnFilters = hasActiveLincolnFilters(
    isLincoln,
    filterControls.selectedFilters
  );

  useEffect(() => {
    if (
      shouldLoadNextLincolnPage(
        hasLincolnFilters,
        hasNextPage,
        isFetchingNextPage,
        isFetchNextPageError
      )
    ) {
      fetchNextPage();
    }
  }, [
    hasLincolnFilters,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  ]);

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
      OrdersPageAnalytics.trackLoadMoreAction(user);
    }
  };

  return (
    <DrawerProvider>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gap={4}
        sx={{ mb: 4 }}
      >
        <Box
          component="section"
          data-testid="orders-section"
          gridColumn="span 12"
          display="grid"
          gridTemplateColumns={
            isTablet
              ? "minmax(0, 1fr)"
              : `${layout.sidebarWidth} minmax(0, 1fr)`
          }
          gap={4}
          sx={layout.sectionSx}
        >
          <Box sx={{ gridColumn: "1 / -1" }}>
            <EMPBreadcrumbs
              t={t}
              customerHandlingLabel={
                isLincoln
                  ? t("GlobalNavBar.customerHandling")
                  : "Customer Handling"
              }
            />
          </Box>
          {(isLincoln || isCanadianPreorder) && (
            <OrdersPageTitle
              title={
                isCanadianPreorder
                  ? t("CanadianPreorderOrders.title", {
                      defaultValue: "Customer Handling Table",
                    })
                  : t("CustomerOrders.ordersPageTitle")
              }
            />
          )}
          <OrdersFilters
            isLoading={isLoading}
            onFiltersApplied={handleFilters}
            isError={isError}
            filterControls={filterControls}
          />

          <Box gridColumn={isTablet ? "1" : "2"} sx={{ minWidth: 0 }}>
            {isLoading ? (
              <OrderTableSkeleton data-testid="order-table-skeleton" />
            ) : (
              <>
                <OrdersSorting
                  disabled={isError || sortedOrders?.length === 0}
                  totalOrders={sortedOrders?.length || 0}
                  onSortChange={handleSortChange}
                  sortValue={sortValue}
                  selectedFilters={
                    isLincoln ? filterControls.selectedFilters : undefined
                  }
                  onFilterRemove={(category, value) =>
                    filterControls.handleFilterChange(category, value, false)
                  }
                  onFilterClear={filterControls.handleClearFilters}
                  filtersDisabled={isLoading || isError}
                />
                <OrdersTable
                  orders={sortedOrders}
                  isError={isError}
                  refetch={refetch}
                  isLoading={isLoading}
                />

                {hasNextPage && (
                  <LoadMoreButton
                    layout={layout}
                    onClick={handleLoadMore}
                    disabled={isFetchingNextPage}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </DrawerProvider>
  );
};

export default Orders;
